import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store';
import {
  LayoutDashboard, Users, Package, ShoppingBag, Wallet, MessageSquare,
  MessageCircle, Settings, LogOut, ChevronLeft, Menu, Home, Bell, Bot, AlertTriangle, Crown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';

const sidebarItems = [
  { path: '/admin', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/admin/users', icon: Users, label: 'Users' },
  { path: '/admin/products', icon: Package, label: 'Products' },
  { path: '/admin/stock', icon: AlertTriangle, label: 'Stock Management' },
  { path: '/admin/orders', icon: ShoppingBag, label: 'Orders' },
  { path: '/admin/deposits', icon: Wallet, label: 'Deposits' },
  { path: '/admin/disputes', icon: MessageSquare, label: 'Disputes' },
  { path: '/admin/vip', icon: Crown, label: 'VIP Management' },
  { path: '/admin/notifications', icon: Bell, label: 'Broadcasts' },
  { path: '/admin/chat', icon: MessageCircle, label: 'Live Chat' },
  { path: '/admin/ai-assistant', icon: Bot, label: 'AI Assistant' },
  { path: '/admin/settings', icon: Settings, label: 'Settings' },
];

function SidebarContent({ onItemClick }) {
  const location = useLocation();
  
  return (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="p-4 border-b border-white/10">
        <Link to="/admin" className="flex items-center gap-2">
          <img 
            src="/images/logo.png" 
            alt="CrackBox" 
            className="w-10 h-10 rounded-lg object-contain"
          />
          <div>
            <span className="text-lg font-bold text-white font-['Outfit']">Admin</span>
            <p className="text-xs text-zinc-500">CrackBox</p>
          </div>
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1">
        {sidebarItems.map((item) => (
          <Link key={item.path} to={item.path} onClick={onItemClick}>
            <Button
              variant="ghost"
              className={`w-full justify-start gap-3 ${
                location.pathname === item.path
                  ? 'bg-[#FFD700]/10 text-[#FFD700] border border-[#FFD700]/30'
                  : 'text-zinc-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </Button>
          </Link>
        ))}
      </nav>

      {/* Back to Site */}
      <div className="p-4 border-t border-white/10">
        <Link to="/">
          <Button variant="outline" className="w-full gap-2 border-white/10">
            <Home className="w-4 h-4" />
            Back to Site
          </Button>
        </Link>
      </div>
    </div>
  );
}

export default function AdminLayout({ children }) {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-[#050505] flex">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex w-64 flex-col border-r border-white/10 bg-[#0A0A0A]">
        <SidebarContent />
      </aside>

      {/* Main Area */}
      <div className="flex-1 flex flex-col">
        {/* Top Bar */}
        <header className="h-16 border-b border-white/10 bg-black/40 backdrop-blur-xl flex items-center justify-between px-4 lg:px-6">
          <div className="flex items-center gap-4">
            {/* Mobile Menu */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="icon">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="bg-[#0A0A0A] border-white/10 p-0 w-64">
                <SidebarContent onItemClick={() => setMobileOpen(false)} />
              </SheetContent>
            </Sheet>

            <h1 className="text-lg font-semibold text-white font-['Outfit']">Admin Panel</h1>
          </div>

          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-sm font-medium text-white">{user?.username}</p>
              <p className="text-xs text-zinc-500">Administrator</p>
            </div>
            <Button variant="ghost" size="icon" onClick={handleLogout} className="text-zinc-400 hover:text-red-400">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 p-4 lg:p-6 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
